import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    IconButton,
    Menu
} from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import Led from '@components/Utilities/Led';
import BranchOfficesService from '@api/BranchOffices/BranchOfficeCtrl';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { ModalSaveBranchOffice } from '@components/Admin/BranchOffices/Modals/ModalAddBranchOffice';
import { ModalViewBranchOffice } from './components/ModalViewBranchOffice';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import Grid from '@mui/material/Grid';
import CustomButton from '@components/Utilities/Buttons/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setBranchOfficeSelected } from '@store/authStore';
import { refreshBranchOffices } from '@utils/Catalogs';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import { setActivityDrawer } from '@store/appStore';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';

const ListBranchOfficesScreen: React.FunctionComponent = () => {
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const { distributionCenters } = useSelector((state: RootReducer) => state.krispis);
    const branchOfficesService = useMemo(() => new BranchOfficesService(), []);
    const [branchOffices, setBranchOfficesData] = useState<BranchOffice[]>([]);
    const [currentBranchOffice, setCurrentBranchOffice] = useState<BranchOffice | undefined>(undefined);
    const modalAddBranchOffice = useOpenModal();
    const [filters, setFilters] = useState({
        search: '',
        distributionCenter: 'all'
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedBranchOffice, setSelectedBranchOffice] = useState<BranchOffice | null>(null);
    const open = Boolean(anchorEl);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    // El admin general ve todas y da de alta; el admin de sucursal ("Mi sucursal") ve y
    // edita las suyas, sin alta ni inventario (tiene el suyo en el sidebar). Plan F6.
    const isGeneralAdmin = canCurrentUser(Capability.BranchEditProtected);

    const onOpenModalAddBranchOffice = () => {
        setCurrentBranchOffice(undefined);
        modalAddBranchOffice.toggle();
    };

    const onViewBranchOfficeInfo = () => {
        handleCloseMenu();
        setIsViewModalOpen(true);
    };

    /**
     * La bitácora de la sucursal (M5). Aquí viven las tarifas de envío y los
     * horarios: lo que se le cobra al cliente. Sin renglón derivado — `branch_offices`
     * no trae `createdAt` (medido: ninguna de las colecciones de configuración lo
     * trae), así que el drawer dirá honestamente "sin registro anterior".
     */
    const handleActivity = () => {
        const branchOffice = selectedBranchOffice;
        handleCloseMenu();
        if (!branchOffice?.id) return;
        dispatch(setActivityDrawer({ isOpen: true, section: 'branchOffice', sectionId: branchOffice.id }));
    };

    const onEditBranchOffice = () => {
        if (selectedBranchOffice) {
            setCurrentBranchOffice(selectedBranchOffice);
            modalAddBranchOffice.toggle();
        }
        handleCloseMenu();
    };

    const handleToggleStatus = async () => {
        handleCloseMenu();
        if (selectedBranchOffice?.id) {
            showLoader();
            try {
                await branchOfficesService.toggleStatus(selectedBranchOffice.id);
                showAlertMessage(`Sucursal ${selectedBranchOffice.status ? 'inactivada' : 'activada'} exitosamente`);
                await getData();
                await refreshBranchOffices();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const handleViewInventory = () => {
        handleCloseMenu();
        if (selectedBranchOffice?.id) {
            // Ver el inventario de una sucursal la vuelve la sucursal EN OPERACIÓN:
            // así la barra superior, el sidebar y la pantalla dicen lo mismo, y
            // cambiarla desde el menú recarga el inventario (antes no reaccionaba)
            dispatch(setBranchOfficeSelected(selectedBranchOffice));
            navigation('inventory', {
                state: {
                    branchOfficeId: selectedBranchOffice.id,
                    distributionCenterId: selectedBranchOffice.distributionCenterId
                }
            });
        }
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, branchOffice: BranchOffice) => {
        setAnchorEl(event.currentTarget);
        setSelectedBranchOffice(branchOffice);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const onAddBranchOffice = async (branchOffice: BranchOffice) => {
        showLoader();
        try {
            await branchOfficesService.saveBranchOffice(branchOffice);
            showAlertMessage('Sucursal guardada exitosamente');
            await getData();
            await refreshBranchOffices();
            modalAddBranchOffice.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };
    // Local, filterable working set for this management table. The global slice
    // (read by other screens) is refreshed separately, only after mutations.
    const getData = async () => {
        showLoader();
        try {
            const data = await branchOfficesService.getBranchOffices(filters);
            setBranchOfficesData(
                data.filter((branchOffice) => canCurrentUser(Capability.BranchEdit, { branchOfficeId: branchOffice.id }))
            );
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    useEffect(() => {
        void getData();
    }, [filters.distributionCenter]);

    const columns = useMemo<ResponsiveGridColDef<BranchOffice>[]>(
        () => [
            {
                field: 'number',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Número
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.number}</Typography>
            },
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Sucursal
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'phone',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Teléfono
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.phone}</Typography>
            },
            {
                field: 'manager',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Responsable
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.manager.name}</Typography>
            },
            {
                field: 'pickupDelivery',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Pickup / Delivery
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Stack direction="row" spacing={2}>
                        <Led value={row.hasPickup} /> <Led value={row.hasDelivery} />
                    </Stack>
                )
            },
            {
                field: 'status',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => <Led value={row.status} />
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton
                        onClick={(e) => handleOpenMenu(e, row)}
                        size="small"
                        aria-controls={open ? 'actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        [open]
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%" spacing={2}>
                <Stack>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3} />
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <Select
                                    labelId="distribution-center-filter-select-label"
                                    id="distribution-center-filter-select"
                                    value={filters.distributionCenter}
                                    onChange={(e) => setFilters({ ...filters, distributionCenter: e.target.value })}
                                >
                                    <MenuItem value="all">Todas las cedis</MenuItem>
                                    {distributionCenters.map((distributionCenter, index) => (
                                        <MenuItem key={index} value={distributionCenter.id}>
                                            {distributionCenter.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3} display={isGeneralAdmin ? undefined : 'none'}>
                            <CustomButton
                                fullWidth
                                variant="contained"
                                color="primary"
                                endIcon={<AddIcon />}
                                onClick={onOpenModalAddBranchOffice}
                            >
                                Nueva sucursal
                            </CustomButton>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar sucursal"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                </Stack>
                <ResponsiveDataGrid
                    searchValue={filters.search}
                    rows={branchOffices}
                    columns={columns}
                    onCellDoubleClick={(cel) => {
                        setSelectedBranchOffice(cel.row as BranchOffice);
                        setIsViewModalOpen(true);
                    }}
                />
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleCloseMenu}
                    MenuListProps={{
                        'aria-labelledby': 'actions-button'
                    }}
                >
                    <MenuItem onClick={onViewBranchOfficeInfo}>Ver</MenuItem>
                    <MenuItem onClick={onEditBranchOffice}>Editar</MenuItem>
                    <MenuItem onClick={handleToggleStatus}>
                        {selectedBranchOffice?.status ? 'Desactivar' : 'Activar'}
                    </MenuItem>
                    {isGeneralAdmin && <MenuItem onClick={handleViewInventory}>Ver inventario</MenuItem>}
                    <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                </Menu>
                <ModalSaveBranchOffice
                    modalAddBranchOffice={modalAddBranchOffice}
                    branchOffice={currentBranchOffice}
                    onAddBranchOffice={onAddBranchOffice}
                />
                <ModalViewBranchOffice
                    modalOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    branchOffice={selectedBranchOffice || undefined}
                />
            </Stack>
        </Box>
    );
};
export default ListBranchOfficesScreen;
